import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import {
  ArrowDown, ArrowUp, Download, ExternalLink, Search, SearchX, TrendingUp, Type, Users,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "Components/ui/card";
import {
  PageBody, PageDescription, PageHeader, PageHeaderRow, PageShell, PageTitle,
} from "Components/ui/page-shell";
import { Skeleton } from "Components/ui/skeleton";
import axiosInstance from "../../../redux/api";
import DateRangePicker from "../DateRangePicker";
import WhoSearched from "./WhoSearched";
import {
  PRESETS, countChange, filterRows, formatDay, formatRange, lastSearched, percentChange, shareOf, sortRows,
  storeLink, toCsv,
} from "./searchesView";
import "./CustomerSearches.css";

// Customer → Searches: what customers typed in search on the website and the
// app, for a date range against the same number of days just before it. The
// server counts the word a customer settled on, not every letter typed.

export const PAGE_ROWS = 25;

const TEAL = "#4B7A8E";
const RED = "#D14343";
const GREEN = "#2E7D5B";
const MUTED = "#8A9BA8";
const INK = "#25343F";

const number = (value) => Number(value || 0).toLocaleString("en-US");

const trend = (row) => (row.searches > row.previous ? "up" : row.searches < row.previous ? "down" : "flat");

// A word in the lists: a click shows who searched it.
const Word = ({ term, onWho }) => (
  <button type="button" className="cs-word" dir="auto" onClick={() => onWho(term)} title="See who searched this">
    {term}
  </button>
);

const StoreLink = ({ term }) => (
  <a className="cs-store-link" href={storeLink(term)} target="_blank" rel="noopener noreferrer"
     aria-label={`View “${term}” on the store`} title="See what customers see">
    <ExternalLink size={14} />
  </a>
);

// One table per tab. ``sorts`` are the columns a click can sort by; ``csv``
// is the spreadsheet the Export button downloads for that tab.
const TABS = [
  {
    key: "top",
    label: "Top searches",
    icon: Search,
    list: (data) => data.top_searches || [],
    empty: "No searches in these dates.",
    sorts: {
      searches: (row) => row.searches,
      results: (row) => row.results,
      change: (row) => row.searches - row.previous,
    },
    head: [["#"], ["Search"], ["Times", "searches"], ["Share"], ["Results", "results"], ["vs before", "change"], [""]],
    cells: (row, index, total, top, onWho) => [
      index + 1,
      <Word term={row.term} onWho={onWho} />,
      number(row.searches),
      <div className="cs-share">
        <div className="cs-share-bar"><span style={{ width: `${(row.searches * 100) / (top || 1)}%` }} /></div>
        {shareOf(row.searches, total)}
      </div>,
      <span style={{ color: row.results === 0 ? RED : INK }}>{number(row.results)}</span>,
      <span className={`cs-change cs-change-${trend(row)}`}>{countChange(row.searches, row.previous)}</span>,
      <StoreLink term={row.term} />,
    ],
    csv: [
      ["Search", (row) => row.term], ["Times", (row) => row.searches], ["Results", (row) => row.results],
      ["Times before", (row) => row.previous], ["Last searched", (row) => lastSearched(row.last_searched)],
    ],
  },
  {
    key: "nothing",
    label: "Found nothing",
    icon: SearchX,
    list: (data) => data.no_result_searches || [],
    empty: "Every search found something in these dates.",
    sorts: {
      searches: (row) => row.searches,
      last: (row) => row.last_searched,
    },
    head: [["#"], ["Search"], ["Times", "searches"], ["Last searched", "last"], ["Did you mean"], [""]],
    cells: (row, index, total, top, onWho) => [
      index + 1,
      <Word term={row.term} onWho={onWho} />,
      number(row.searches),
      <span style={{ color: MUTED }}>{lastSearched(row.last_searched)}</span>,
      row.did_you_mean ? (
        <a className="cs-suggestion" dir="auto" href={storeLink(row.did_you_mean)} target="_blank"
           rel="noopener noreferrer" title="Open the store's results for this word">
          {row.did_you_mean}
        </a>
      ) : <span style={{ color: MUTED }}>—</span>,
      <StoreLink term={row.term} />,
    ],
    csv: [
      ["Search", (row) => row.term], ["Times", (row) => row.searches],
      ["Last searched", (row) => lastSearched(row.last_searched)], ["Did you mean", (row) => row.did_you_mean || ""],
    ],
  },
  {
    key: "rising",
    label: "Rising",
    icon: TrendingUp,
    list: (data) => data.rising_searches || [],
    empty: "Nothing searched more than in the period before.",
    sorts: {
      searches: (row) => row.searches,
      previous: (row) => row.previous,
      change: (row) => row.searches - row.previous,
    },
    head: [["#"], ["Search"], ["Times", "searches"], ["Before", "previous"], ["Change", "change"], [""]],
    cells: (row, index, total, top, onWho) => [
      index + 1,
      <Word term={row.term} onWho={onWho} />,
      number(row.searches),
      <span style={{ color: MUTED }}>{number(row.previous)}</span>,
      <span className="cs-change cs-change-up">{countChange(row.searches, row.previous)}</span>,
      <StoreLink term={row.term} />,
    ],
    csv: [
      ["Search", (row) => row.term], ["Times", (row) => row.searches], ["Times before", (row) => row.previous],
    ],
  },
];

function Stat({ label, value, detail, change, upIsBad, icon: Icon }) {
  const good = change && change.direction !== "flat" && ((change.direction === "up") !== Boolean(upIsBad));
  const color = !change || change.direction === "flat" ? MUTED : good ? GREEN : RED;
  const Arrow = change?.direction === "down" ? ArrowDown : ArrowUp;
  return (
    <Card className="h-100">
      <CardContent className="p-4">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <p className="cs-stat-label">{label}</p>
            <p className="cs-stat-value">
              {value}
              {detail && <span className="cs-stat-detail">{detail}</span>}
            </p>
            <p className="cs-stat-change" style={{ color }}>
              {change ? (
                <>
                  {change.direction !== "flat" && <Arrow size={13} />} {change.text}{" "}
                  <span style={{ color: MUTED }}>vs period before</span>
                </>
              ) : <span style={{ color: MUTED }}>Nothing to compare yet</span>}
            </p>
          </div>
          <div className="cs-stat-icon"><Icon size={20} color={TEAL} /></div>
        </div>
      </CardContent>
    </Card>
  );
}

function SearchesChart({ days }) {
  const data = days.map((day) => ({
    day: formatDay(day.date),
    found: day.searches - day.found_nothing,
    nothing: day.found_nothing,
  }));
  return (
    <Card>
      <CardHeader className="d-flex flex-row align-items-center justify-content-between">
        <CardTitle style={{ fontSize: "16px" }}>Searches per day</CardTitle>
        <div className="cs-legend">
          <span><i style={{ background: TEAL }} /> Found results</span>
          <span><i style={{ background: RED }} /> Found nothing</span>
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={data} barSize={data.length > 20 ? 10 : 22}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EAEFEF" vertical={false} />
            <XAxis dataKey="day" tick={{ fontSize: 11, fill: MUTED }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: MUTED }} />
            <Tooltip formatter={(value, name) => [value, name === "found" ? "Found results" : "Found nothing"]} />
            <Bar dataKey="found" stackId="searches" fill={TEAL} />
            <Bar dataKey="nothing" stackId="searches" fill={RED} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

function SearchesTable({ data, onWho }) {
  const [tabKey, setTabKey] = useState("top");
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState({ key: "searches", direction: "desc" });
  const [limit, setLimit] = useState(PAGE_ROWS);
  const tab = TABS.find((candidate) => candidate.key === tabKey);
  const total = data.summary?.searches || 0;

  const rows = useMemo(() => {
    const value = tab.sorts[sort.key] || tab.sorts.searches;
    return sortRows(filterRows(tab.list(data), filter), value, sort.direction);
  }, [tab, data, filter, sort]);
  const top = Math.max(0, ...tab.list(data).map((row) => row.searches));

  const choose = (key) => {
    setTabKey(key);
    setSort({ key: "searches", direction: "desc" });
    setLimit(PAGE_ROWS);
  };
  const sortBy = (key) => {
    setSort((current) => ({ key, direction: current.key === key && current.direction === "desc" ? "asc" : "desc" }));
  };
  const download = () => {
    const blob = new Blob([toCsv(rows, tab.csv)], { type: "text/csv;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `searches-${tab.key}-${data.start_date}-to-${data.end_date}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(link.href);
  };

  return (
    <Card>
      <CardHeader className="cs-table-head">
        <div role="tablist" aria-label="Search lists" className="cs-tabs">
          {TABS.map(({ key, label, icon: Icon, list }) => (
            <button key={key} type="button" role="tab" aria-selected={key === tabKey}
                    className={`cs-tab${key === tabKey ? " is-active" : ""}`} onClick={() => choose(key)}>
              <Icon size={15} /> {label} <span className="cs-tab-count">{list(data).length}</span>
            </button>
          ))}
        </div>
        <div className="d-flex gap-2 align-items-center">
          <input type="search" className="cs-filter" placeholder="Filter words…" aria-label="Filter words"
                 value={filter} onChange={(event) => { setFilter(event.target.value); setLimit(PAGE_ROWS); }} />
          <button type="button" className="cs-button" onClick={download} disabled={rows.length === 0}>
            <Download size={14} /> Export
          </button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {rows.length === 0 ? (
          <p className="cs-empty">{filter ? `No words match “${filter}”.` : tab.empty}</p>
        ) : (
          <table className="cs-table" aria-label={tab.label}>
            <thead>
              <tr>
                {tab.head.map(([heading, key], index) => (
                  <th key={heading || index} aria-sort={key && sort.key === key
                    ? (sort.direction === "desc" ? "descending" : "ascending") : undefined}>
                    {key ? (
                      <button type="button" className="cs-sort" onClick={() => sortBy(key)}>
                        {heading}{sort.key === key && (sort.direction === "desc" ? " ↓" : " ↑")}
                      </button>
                    ) : heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, limit).map((row, index) => (
                <tr key={row.term}>
                  {tab.cells(row, index, total, top, onWho).map((cell, column) => <td key={column}>{cell}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {rows.length > limit && (
          <button type="button" className="cs-more" onClick={() => setLimit((shown) => shown + PAGE_ROWS)}>
            Show {Math.min(PAGE_ROWS, rows.length - limit)} more of {rows.length - limit}
          </button>
        )}
      </CardContent>
    </Card>
  );
}

export default function CustomerSearches() {
  const [params, setParams] = useState({ date_filter: "last_7_days", start_date: "", end_date: "" });
  const [state, setState] = useState({ status: "loading", data: null, error: "" });
  const [who, setWho] = useState(null);
  // Only the answer to the latest request may land: changing the dates while
  // an older request is still out must not show the older range.
  const latest = useRef(0);

  const load = useCallback(() => {
    const request = ++latest.current;
    setState((previous) => ({ ...previous, status: "loading", error: "" }));
    axiosInstance
      .get("admin/customers/searches", { params })
      .then((response) => {
        if (request !== latest.current) return;
        setState({ status: "ready", data: response?.data?.response || null, error: "" });
      })
      .catch((error) => {
        if (request !== latest.current) return;
        setState({ status: "error", data: null, error: error?.response?.data?.message || "Couldn't load searches." });
      });
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  const { status, data, error } = state;
  const now = data?.summary || {};
  const before = data?.previous_summary || {};

  return (
    <PageShell className="customer-searches">
      <PageHeader>
        <PageHeaderRow>
          <div>
            <PageTitle>Customer searches</PageTitle>
            <PageDescription>What customers typed in search on the website and the app.</PageDescription>
          </div>
          <div className="d-flex flex-wrap align-items-center gap-2">
            <div className="cs-presets" role="group" aria-label="Date range">
              {PRESETS.map(({ key, label }) => (
                <button key={key} type="button" aria-pressed={params.date_filter === key}
                        className={`cs-preset${params.date_filter === key ? " is-active" : ""}`}
                        onClick={() => setParams({ date_filter: key, start_date: "", end_date: "" })}>
                  {label}
                </button>
              ))}
            </div>
            <DateRangePicker onApply={(range) => setParams(range)} />
          </div>
        </PageHeaderRow>
      </PageHeader>

      <PageBody>
        <p className="cs-range" aria-live="polite">
          {status === "ready" && data
            ? <>{formatRange(data.start_date, data.end_date)} <span>· compared with {formatRange(
                data.previous_start_date, data.previous_end_date)}</span></>
            : " "}
        </p>

        {status === "error" && (
          <Card>
            <CardContent className="d-flex align-items-center justify-content-between p-4" role="alert">
              <span style={{ color: RED }}>{error}</span>
              <button type="button" className="cs-button" onClick={load}>Try again</button>
            </CardContent>
          </Card>
        )}

        {status === "loading" && (
          <div data-testid="searches-loading" className="row g-3">
            {[0, 1, 2, 3].map((key) => (
              <div key={key} className="col-6 col-xl-3"><Skeleton style={{ height: "112px" }} /></div>
            ))}
            <div className="col-12"><Skeleton style={{ height: "280px" }} /></div>
          </div>
        )}

        {status === "ready" && data && (
          <>
            <div className="row g-3 mb-3">
              <div className="col-6 col-xl-3">
                <Stat label="Searches" value={number(now.searches)} icon={Search}
                      change={percentChange(now.searches, before.searches)} />
              </div>
              <div className="col-6 col-xl-3">
                <Stat label="People searching" value={number(now.people)} icon={Users}
                      change={percentChange(now.people, before.people)} />
              </div>
              <div className="col-6 col-xl-3">
                <Stat label="Found nothing" value={number(now.found_nothing)} icon={SearchX} upIsBad
                      detail={`${shareOf(now.found_nothing, now.searches)} of searches`}
                      change={percentChange(now.found_nothing, before.found_nothing)} />
              </div>
              <div className="col-6 col-xl-3">
                <Stat label="Different words" value={number(now.words)} icon={Type}
                      change={percentChange(now.words, before.words)} />
              </div>
            </div>

            {now.searches ? (
              <>
                <div className="mb-3"><SearchesChart days={data.days || []} /></div>
                <SearchesTable key={`${data.start_date}-${data.end_date}`} data={data} onWho={setWho} />
              </>
            ) : (
              <Card>
                <CardContent className="cs-nothing-yet">
                  <Search size={40} strokeWidth={1.5} />
                  <p>No searches in these dates yet.</p>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </PageBody>
      <WhoSearched term={who} params={params} onClose={() => setWho(null)} />
    </PageShell>
  );
}
